import { useEffect, useRef, useState } from "react";
import { ImagePlus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

interface BrandAssetUploaderProps {
  label: string;
  currentUrl?: string | null;
  isUploading: boolean;
  onUpload: (file: File) => Promise<void>;
  onRemove: () => void;
  uploadLabel: string;
  removeLabel: string;
}

export function BrandAssetUploader({
  label,
  currentUrl,
  isUploading,
  onUpload,
  onRemove,
  uploadLabel,
  removeLabel,
}: BrandAssetUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);

  useEffect(() => () => {
    if (preview) URL.revokeObjectURL(preview);
  }, [preview]);

  useEffect(() => {
    if (currentUrl) setPreview(null);
  }, [currentUrl]);

  const selectFile = async (file?: File) => {
    if (!file) return;
    const localUrl = URL.createObjectURL(file);
    setPreview(localUrl);
    try {
      await onUpload(file);
    } catch {
      setPreview(null);
    } finally {
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="relative aspect-[16/7] overflow-hidden rounded-md border bg-muted">
        {preview || currentUrl ? (
          <img src={preview ?? currentUrl} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full items-center justify-center text-muted-foreground"><ImagePlus className="h-7 w-7" /></div>
        )}
      </div>
      <div className="flex gap-2">
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          disabled={isUploading}
          onChange={(event) => selectFile(event.target.files?.[0])}
        />
        <Button type="button" variant="outline" size="sm" disabled={isUploading} onClick={() => inputRef.current?.click()}>
          <ImagePlus className="h-4 w-4" />{uploadLabel}
        </Button>
        {(currentUrl || preview) && (
          <Button type="button" variant="ghost" size="icon-sm" aria-label={removeLabel} onClick={onRemove}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        )}
      </div>
    </div>
  );
}